import React, { useState, useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import callApi from "../helpers/callApi";
import { Button, CircularProgress, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PendingIcon from "@mui/icons-material/Pending";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteForever from "@mui/icons-material/DeleteForever";
import ScriptVersionsTable from "../components/ScriptVersionsTable";
import NewReleaseForm from "./NewReleaseForm";
import useInterval from "../hooks/useInterval";
import useWindowFocus from "../hooks/useWindowFocus";
import { renderReleasedIcon } from "./pipelineStageStatus";

const STAGE_SUCCEEDED = "succeeded";
const STAGE_FAILED = "failed";
const STAGE_PENDING = "pending";

const PipelineTable = (params) => {
  const { auth, scriptName, scriptsEndpoint, pipelinesEndpoint } = params;
  const [scriptPipelines, setScriptPipelines] = useState([]);
  const [hasActive, setHasActive] = useState(false);
  const [completionEventCount, setCompletionEventCount] = useState(0);

  const hasFocus = useWindowFocus();

  const renderReleased = (params) => renderReleasedIcon(params.value);

  const renderPipelineActions = (params) => {
    if (params.value?.retryable && params.value?.deletable) {
      return (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Retry from first failed stage">
            <Button size="small" variant="contained" color="primary" onClick={() => retryPipeline(params.row.id)}>
              <ReplayIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="Delete pipeline (does not delete script version)">
            <Button size="small" variant="contained" color="error" onClick={() => deletePipeline(params.row.id)}>
              <DeleteForever fontSize="small" />
            </Button>
          </Tooltip>
        </Stack>
      );
    } else if (params.value?.retryable) {
      return (
        <Tooltip title="Retry from first failed stage">
          <Button size="small" variant="contained" color="primary" onClick={() => retryPipeline(params.row.id)}>
            <ReplayIcon fontSize="small" />
          </Button>
        </Tooltip>
      );
    } else if (params.value?.deletable) {
      return (
        <Tooltip title="Delete pipeline (does not delete script version)">
          <Button size="small" variant="contained" color="error" onClick={() => deletePipeline(params.row.id)}>
            <DeleteForever fontSize="small" />
          </Button>
        </Tooltip>
      );
    } else {
      return <DoNotDisturbIcon color="disabled" fontSize="small" />;
    }
  };

  const renderStatus = (params) => {
    if (params.value === STAGE_SUCCEEDED) {
      return <CheckCircleIcon color="success" fontSize="small" />;
    } else if (params.value === STAGE_FAILED) {
      return <ErrorIcon color="error" fontSize="small" />;
    } else if (params.value === STAGE_PENDING) {
      if (params.row.active) {
        return <CircularProgress size={18} color="info" />;
      } else {
        return <PendingIcon color="info" fontSize="small" />;
      }
    }
    return null;
  };

  const pipelineColumns = [
    { field: "id", headerName: "ID", width: 150 },
    { field: "started_by", headerName: "Started By", width: 100 },
    { field: "started_at", headerName: "Started At", width: 150 },
    {
      field: "import_status",
      headerName: "Import?",
      width: 100,
      renderCell: renderStatus,
      align: "center",
      headerAlign: "center",
      display: "flex",
    },
    {
      field: "rubric_config_status",
      headerName: "Config?",
      width: 100,
      renderCell: renderStatus,
      align: "center",
      headerAlign: "center",
      display: "flex",
    },
    { field: "rubric_name", headerName: "Rubric Name", width: 300 },
    { field: "rubric_version", headerName: "Rubric Version", width: 150 },
    {
      field: "embeddings_status",
      headerName: "Embeds?",
      width: 100,
      renderCell: renderStatus,
      align: "center",
      headerAlign: "center",
      display: "flex",
    },
    {
      field: "rubric_testing_status",
      headerName: "MQ Test?",
      width: 100,
      renderCell: renderStatus,
      align: "center",
      headerAlign: "center",
      display: "flex",
    },
    { field: "script_version", headerName: "Version", width: 100 },
    {
      field: "script_released",
      headerName: "Released?",
      width: 100,
      renderCell: renderReleased,
      align: "center",
      headerAlign: "center",
      display: "flex",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: renderPipelineActions,
      sortable: false,
      display: "flex",
    },
  ];

  // update table when component mounted with different scriptName
  useEffect(() => {
    getPipelines();
  }, [scriptName]);

  // update table on timer when there are active pipeline jobs
  useInterval(() => {
    if (hasActive && hasFocus) {
      getPipelines();
    }
  }, 10000);

  const getPipelines = async () => {
    try {
      const pipelinesResponse = await callApi(
        `${pipelinesEndpoint}/${scriptName}`,
        "GET",
        undefined,
        auth.user?.access_token,
      );
      let activeCount = 0;
      for (const r of pipelinesResponse) {
        r.id = r.pipeline_id;
        const releasable =
          r.import_status === STAGE_SUCCEEDED &&
          r.rubric_config_status === STAGE_SUCCEEDED &&
          r.embeddings_status === STAGE_SUCCEEDED &&
          r.rubric_testing_status === STAGE_SUCCEEDED &&
          !r.script_released;
        const retryable =
          r.import_status === STAGE_FAILED ||
          r.rubric_config_status === STAGE_FAILED ||
          r.embeddings_status === STAGE_FAILED ||
          r.rubric_testing_status === STAGE_FAILED;
        const deletable = r.import_status === STAGE_FAILED || !r.active;
        if (r.active) {
          activeCount += 1;
        }
        r.actions = {
          releasable,
          retryable,
          deletable,
        };
      }
      setScriptPipelines(pipelinesResponse);

      // if there were pipelines
      if (pipelinesResponse?.length > 0) {
        // if any pipelines active, update state to enable refreshing the table
        if (activeCount > 0) {
          setHasActive(true);
        }
        // if no pipelines active
        else {
          // if previously there was an active pipeline, force refresh of ScriptVersionsTable
          if (hasActive) {
            setCompletionEventCount(completionEventCount + 1);
          }
          // update state to disable refreshing the table
          setHasActive(false);
        }
      }
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
  };

  const retryPipeline = async (pipelineId) => {
    try {
      const response = await callApi(`${pipelinesEndpoint}/${pipelineId}`, "PUT", undefined, auth.user?.access_token);
      setHasActive(true);
      enqueueSnackbar(response, { variant: "info" });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
  };

  const newPipeline = async (scriptName, rubricName, rubricVersion) => {
    try {
      const body = {
        rubricName,
        rubricVersion,
        requestedBy: auth.user?.profile.email,
      };
      const response = await callApi(`${pipelinesEndpoint}/${scriptName}`, "POST", body, auth.user?.access_token);
      setHasActive(true);
      enqueueSnackbar(response, { variant: "info" });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
  };

  const deletePipeline = async (pipelineId) => {
    try {
      const response = await callApi(
        `${pipelinesEndpoint}/${pipelineId}`,
        "DELETE",
        undefined,
        auth.user?.access_token,
      );
      if (!response?.deleted) {
        enqueueSnackbar("Failed to delete pipeline", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
    await getPipelines();
  };

  return (
    <Stack direction="column" spacing={4}>
      <ScriptVersionsTable
        auth={auth}
        scriptName={scriptName}
        scriptsEndpoint={scriptsEndpoint}
        key={completionEventCount}
      />
      <Divider />
      <Stack direction="column" spacing={2}>
        <Typography variant="h6">Release Pipeline Job History</Typography>
        <NewReleaseForm scriptName={scriptName} onSubmit={newPipeline} />
        <DataGrid
          rows={scriptPipelines}
          columns={pipelineColumns}
          autoHeight
          density="comfortable"
          initialState={{
            sorting: {
              sortModel: [{ field: "started_at", sort: "desc" }],
            },
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          sx={{ border: 0 }}
        />
      </Stack>
    </Stack>
  );
};

export default PipelineTable;
